// ═══ 업무별 기록 v5 — 장부 반영 카드 목록 (DESIGN_V5 §0·§1.1·§6.1) ═══
// 엑셀 시트를 묶음(카드)으로 나눠, 묶음 안 기록을 모두 업체에 연결하면 [장부에 반영] 한 번으로 장부에 넣는다.
// 카드마다 대사표(전월미수 → 이 묶음 순액 → 미수, 다음 묶음과 이어짐) · 상태 · 장부에서 고친 기록 · ERP 겹침 · 확인 권장 ·
// 예전 방식(v2) 미수 맞춤 취소 · 다시 발행할 정산서 를 보여 준다. 반영/취소는 업체별 확인 창(RecordsPreviewModal)을 거친다.
// 서버 계약 (API_V5_CHANGES 반영):
//   GET  /work-records/cards?jobKind= → {success, summary:{excelTotal, postedTotal, remainingGapCount}(요청 업무 기준), cards:[…§6.1…]}
//        detached — 블록의 state 1·2 행 {count, delta}(rows 는 반영됨일 때만) · reentered {count, amount}(블록 밖에 다시 입력된 입금)
//        affectedStatements 는 반영된 카드만
//        carryGap / rowGaps[] = {gapId, no, label, decision|null}
//   POST /work-records/receivables/:id/reverse {reason} → {success} · 409 ALREADY_REVERSED   (v2 잔재 정리 — 카드 v2Active 가 있을 때만)
//
// props: jobKind · reloadKey · userName · showToast · onChanged · onOpenList({tab, sheet, rowFrom, rowTo}) · onOpenGaps
import React, { useState, useEffect } from 'react';
import { is } from './SettlementItemDrawer';
import { formatArrivedAtKst } from '../utils/dateKst';
import RecordsPreviewModal, { fmt, num, signed, signColor, getJson, postJson, CHOICE_LABEL, DETACH_LABEL, KIND_LABEL, Statements } from './RecordsPreviewModal';
import RecordsOverlapModal from './RecordsOverlapModal';

var PAIR_LABEL = { import_same_amount: '이관된 말소 건과 금액·날짜가 비슷함', host_excluded: '업체 시트에 없는 입금' };
var line = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, fontSize: 14 };
var softBox = function (bg, border, color) { return { marginTop: 10, padding: '10px 12px', background: bg, border: '1px solid ' + border, borderRadius: 8, fontSize: 13.5, color: color, lineHeight: 1.6 }; };
var stateBadge = function (posted) {
  return (<span style={{ fontSize: 12.5, fontWeight: 800, padding: '3px 10px', borderRadius: 10, background: posted ? '#dcfce7' : '#f1f5f9', color: posted ? '#166534' : '#475569' }}>{posted ? '장부에 반영됨' : '반영 전'}</span>);
};
var gapText = function (g) {
  var d = g && g.decision;
  return g.no + ' ' + (g.label || '') + ' — ' + (d ? '결정: ' + (CHOICE_LABEL[d.choice] || d.choice) : '아직 정하지 않음');
};

// 대사표 칸 — 제목 / 금액 / 작은 설명
var recon = function (label, amount, sub, strong) {
  return (<div style={{ flex: 1, minWidth: 130 }}>
    <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: strong ? 18 : 16, fontWeight: 800, color: strong ? '#b45309' : '#1B3A5C' }}>{amount}</div>
    {!!sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
  </div>);
};
var arrow = <div style={{ alignSelf: 'center', fontSize: 18, color: '#94a3b8' }}>→</div>;

function V2Reverse(props) {
  var v = props.v2, showToast = props.showToast;
  var [open, setOpen] = useState(false);
  var [reason, setReason] = useState('');
  var [busy, setBusy] = useState(false);
  var run = function () {
    if (busy || !reason.trim()) return;
    setBusy(true);
    postJson('/work-records/receivables/' + v.receivableId + '/reverse', { reason: reason.trim() }).then(function (res) {
      setBusy(false);
      var d = res.data;
      if (res.status === 409 && d.code === 'ALREADY_REVERSED') { showToast('이미 취소된 반영입니다. 화면을 새로 불러왔습니다.', 'err'); props.onChanged(); return; }
      if (!d.success) { showToast(d.error || '취소 실패', 'err'); return; }
      showToast('예전 방식 미수 맞춤 반영을 취소했습니다'); setOpen(false); props.onChanged();
    }).catch(function () { setBusy(false); showToast('서버 오류', 'err'); });
  };
  return (<div data-testid="v2-active" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
    <span>예전 미수 맞춤 {signed(v.amount)}원</span>
    {!open && <button className="btn btn-outline btn-sm" onClick={function () { setOpen(true); }}>예전 반영 취소</button>}
    {open && <React.Fragment>
      <input aria-label="취소 이유" placeholder="취소 이유" value={reason} maxLength={200} style={Object.assign({}, is, { flex: 1, minWidth: 160 })}
        onChange={function (e) { setReason(e.target.value); }} />
      <button className="btn btn-blue btn-sm" disabled={busy || !reason.trim()} onClick={run}>{busy ? '취소 중…' : '취소 확정'}</button>
      <button className="btn btn-outline btn-sm" disabled={busy} onClick={function () { setOpen(false); }}>닫기</button>
    </React.Fragment>}
  </div>);
}

function Card(props) {
  var c = props.card, next = props.next;
  var ex = c.excel || {}, cnt = c.counts || {}, unl = c.unlinked || {}, det = c.detached || {}, ov = c.overlap || {}, re = c.reentered || {};
  var posted = c.state === 'posted';
  var run = c.run;
  var unlinkedN = num(unl.items) + num(unl.deposits) + num(unl.adjusts);
  var v2 = c.v2Active || [];
  var pairs = c.checkPairs || [];
  var rowGaps = c.rowGaps || [];
  var ovTotal = num(ov.pending) + num(ov.excluded) + num(ov.separate);
  var blockers = [];
  if (unlinkedN > 0) blockers.push('업체에 연결하지 않은 기록 ' + fmt(unlinkedN) + '건을 먼저 연결해 주세요');
  if (v2.length) blockers.push('예전 방식 미수 맞춤 반영을 먼저 취소해 주세요');
  if (num(ov.pending) > 0) blockers.push('ERP 에 이미 있는 건 ' + fmt(ov.pending) + '건을 먼저 정해 주세요');
  var openList = function (tab) { props.onOpenList({ tab: tab, sheet: c.sheet, rowFrom: c.rows.from, rowTo: c.rows.to }); };
  var nextLine = c.carryGap
    ? <span style={{ color: '#b45309', fontWeight: 700 }}>다음 묶음으로 넘어가지 않았습니다 → 확인 항목 {c.carryGap.no}</span>
    : (next && next.sheet === c.sheet && num(next.excel && next.excel.open) === num(ex.close) && !!(next.excel && next.excel.openCell)
      ? <span style={{ color: '#047857' }}>다음 묶음 {next.no} 의 전월미수({next.excel.openCell})로 이어집니다</span> : null);

  return (<div data-testid="records-card" aria-label={'카드 ' + c.no} style={{ border: '1px solid ' + (posted ? '#bbf7d0' : '#e2e8f0'), background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 17, fontWeight: 900, color: '#1B3A5C' }}>{c.no}</span>
      <span style={{ fontSize: 16, fontWeight: 800, color: '#334155' }}>{c.label}</span>
      <span style={{ fontSize: 13, color: '#94a3b8' }}>{c.sheet} 시트 {c.rows && c.rows.from}–{c.rows && c.rows.to}행</span>
      <span style={{ marginLeft: 'auto' }}>{stateBadge(posted)}</span>
    </div>

    <div data-testid="card-recon" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12, padding: '10px 12px', background: '#f8fafc', borderRadius: 10 }}>
      {recon('엑셀 전월미수', ex.openCell ? fmt(ex.open) + '원' : '없음', ex.openCell || '')}
      {arrow}
      {recon('이 묶음 순액', signed(c.rowsNet) + '원', '청구 − 입금')}
      {arrow}
      {recon('엑셀 미수', ex.closeCell ? fmt(ex.close) + '원' : '없음', (ex.closeCell || '') + (ex.closeValid === false ? ' · 엑셀 합계 칸 오류(쓰지 않음)' : ''), true)}
    </div>
    {!!nextLine && <div style={line}>{nextLine}</div>}

    <div style={line}>
      <span style={{ color: '#475569' }}>건 {fmt(cnt.items)} · 입금 {fmt(cnt.deposits)}{num(cnt.adjusts) ? ' · 조정 ' + fmt(cnt.adjusts) : ''}{num(cnt.excluded) ? ' · 제외 ' + fmt(cnt.excluded) : ''}</span>
    </div>
    {!posted && <div style={line}>{unlinkedN > 0
      ? <React.Fragment>
        <span style={{ color: '#b45309', fontWeight: 800 }}>업체 연결 안 된 기록 {fmt(unlinkedN)}건</span>
        {num(unl.items) > 0 && <button className="btn btn-outline btn-sm" onClick={function () { openList('items'); }}>미연결 건 {fmt(unl.items)}건 보기</button>}
        {num(unl.deposits) > 0 && <button className="btn btn-outline btn-sm" onClick={function () { openList('deposits'); }}>미연결 입금 {fmt(unl.deposits)}건 보기</button>}
      </React.Fragment>
      : <span style={{ color: '#047857', fontWeight: 800 }}>모든 기록이 업체에 연결되었습니다</span>}</div>}

    {posted && !!run && <div data-testid="card-run" style={softBox('#f0fdf4', '#bbf7d0', '#166534')}>
      <b>{run.createdByName || '-'}{run.createdByStaff ? ' (직원)' : ''}</b> · {formatArrivedAtKst(run.createdAt) || '-'} 반영
      <div>건 {fmt(run.itemCount)} · 청구 {fmt(run.chargeCount)} · 입금 {fmt(run.paymentCount)}{num(run.adjustCount) ? ' · 조정 ' + fmt(run.adjustCount) : ''} · 미수 {signed(run.net)}원</div>
      {!!run.memo && <div>메모: {run.memo}</div>}
    </div>}

    {num(det.count) > 0 && <div data-testid="card-detached" style={softBox('#fffbeb', '#fde68a', '#92400e')}>
      {posted
        ? <span><b>장부에서 직접 고친 기록 {fmt(det.count)}건</b> (엑셀과 차이 <span style={{ color: signColor(det.delta) }}>{signed(det.delta)}원</span>) — 반영을 취소해도 일반 장부 줄로 남습니다.</span>
        : <span><b>지난 반영 취소 때 장부에 남겨 둔 고친 기록 {fmt(det.count)}건</b> (엑셀과 차이 <span style={{ color: signColor(det.delta) }}>{signed(det.delta)}원</span>) — 다시 반영할 때 이 기록은 빠집니다.</span>}
      {(det.rows || []).map(function (r) {
        return <div key={r.recordId + r.kind}>{r.srcRow}행 · {KIND_LABEL[r.kind] || r.kind} · {DETACH_LABEL[r.reason] || r.reason} · {signed(r.delta)}원</div>;
      })}
    </div>}

    {num(re.count) > 0 && <div data-testid="card-reentered" style={softBox('#fffbeb', '#fde68a', '#92400e')}>
      <b>장부에서 입금을 고쳐 이 묶음 밖에 다시 넣은 입금 {fmt(re.count)}건 · {fmt(re.amount)}원</b> — 그만큼 엑셀과 차이가 납니다.
    </div>}

    {ovTotal > 0 && <div data-testid="card-overlap" style={softBox(num(ov.pending) ? '#fffbeb' : '#f8fafc', num(ov.pending) ? '#fde68a' : '#e2e8f0', '#475569')}>
      <b>ERP 에 이미 있음 {fmt(ovTotal)}건</b> — 정하지 않음 {fmt(ov.pending)} · 엑셀 건 빼기 {fmt(ov.excluded)} · 별건 {fmt(ov.separate)}
      {num(ov.amountDiff) !== 0 && <span> · 금액 차 {signed(ov.amountDiff)}원</span>}
      {!posted && <div style={{ marginTop: 6 }}><button className="btn btn-outline btn-sm" onClick={function () { props.onOverlap(c); }}>겹친 건 정하기</button></div>}
    </div>}

    {pairs.length > 0 && <details data-testid="card-pairs" style={{ marginTop: 10, fontSize: 13.5, color: '#475569' }}>
      <summary style={{ cursor: 'pointer' }}>확인해 보면 좋은 기록 {fmt(pairs.length)}건 (반영은 막지 않습니다)</summary>
      {pairs.map(function (p) { return <div key={p.recordId}>{p.srcRow}행 · {p.plate || '-'} · {PAIR_LABEL[p.reason] || p.reason}</div>; })}
    </details>}

    {v2.length > 0 && <div style={softBox('#fef2f2', '#fecaca', '#b91c1c')}>
      예전 방식 '미수 맞춤' 반영이 남아 있어 먼저 취소해야 장부에 반영할 수 있습니다.
      {v2.map(function (v) { return <V2Reverse key={v.receivableId} v2={v} showToast={props.showToast} onChanged={props.onChanged} />; })}
    </div>}

    {(!!c.carryGap || rowGaps.length > 0) && <div data-testid="card-gaps" style={softBox('#f8fafc', '#e2e8f0', '#334155')}>
      {!!c.carryGap && <div>{gapText(c.carryGap)}{!posted ? ' · 반영할 때 함께 정합니다' : ''}</div>}
      {rowGaps.map(function (g) { return <div key={g.gapId}>{gapText(g)}</div>; })}
      {posted && <button className="btn btn-outline btn-sm" style={{ marginTop: 6 }} onClick={props.onOpenGaps}>확인 항목 보기</button>}
    </div>}

    <Statements list={c.affectedStatements} />

    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
      {!posted && <button className="btn btn-blue" style={Object.assign({ minHeight: 44, fontSize: 15, padding: '0 22px' }, blockers.length ? { opacity: 0.45, cursor: 'not-allowed' } : null)}
        disabled={blockers.length > 0} onClick={function () { props.onPreview('post', c); }}>장부에 반영</button>}
      {posted && !!run && <button className="btn btn-outline" style={{ minHeight: 44, fontSize: 15, padding: '0 22px' }} onClick={function () { props.onPreview('undo', c); }}>반영 취소</button>}
      {!posted && blockers.map(function (b, i) { return <span key={i} style={{ fontSize: 13.5, color: '#b45309' }}>{b}</span>; })}
    </div>
  </div>);
}

export default function RecordsCards(props) {
  var jobKind = props.jobKind;
  var showToast = props.showToast || function () {};
  var onChanged = props.onChanged || function () {};
  var [data, setData] = useState(null);
  var [err, setErr] = useState('');
  var [modal, setModal] = useState(null);   // {mode:'post'|'undo'|'overlap', card}

  useEffect(function () {
    var alive = true;
    setErr('');
    getJson('/work-records/cards?jobKind=' + encodeURIComponent(jobKind)).then(function (res) {
      if (!alive) return;
      var d = res.data;
      if (!d.success || !Array.isArray(d.cards)) { setErr(d.error || '조회 실패'); setData({ summary: {}, cards: [] }); return; }
      setData({ summary: d.summary || {}, cards: d.cards });
    }).catch(function () { if (alive) { setErr('조회 실패'); setData({ summary: {}, cards: [] }); } });
    return function () { alive = false; };
  }, [jobKind, props.reloadKey]);

  var cards = (data && data.cards) || [];
  var s = (data && data.summary) || {};
  var done = function () { setModal(null); onChanged(); };

  return (<div>
    <p style={{ margin: '0 0 10px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>
      엑셀 기록을 묶음별로 장부에 넣습니다. 묶음 안 기록을 모두 업체에 연결하면 [장부에 반영]을 누를 수 있고, 누르기 전에 업체별로 들어갈 금액을 먼저 보여 드립니다.
    </p>
    {!!data && <div data-testid="cards-summary" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '12px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 12 }}>
      {recon('엑셀 미수 합계 (' + jobKind + ')', fmt(s.excelTotal) + '원', '이월 안 된 묶음 제외')}
      {recon('장부에 반영된 미수', fmt(s.postedTotal) + '원')}
      {recon('남은 확인 항목', fmt(s.remainingGapCount) + '건')}
    </div>}
    {!!err && <div style={softBox('#fef2f2', '#fecaca', '#b91c1c')}>{err}</div>}
    {data === null && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>조회 중…</div>}
    {!!data && !cards.length && !err && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>반영할 묶음이 없습니다.</div>}
    {cards.map(function (c, i) {
      return <Card key={c.balanceId} card={c} next={cards[i + 1]} showToast={showToast} onChanged={onChanged}
        onOpenList={props.onOpenList || function () {}} onOpenGaps={props.onOpenGaps || function () {}}
        onPreview={function (mode, card) { setModal({ mode: mode, card: card }); }}
        onOverlap={function (card) { setModal({ mode: 'overlap', card: card }); }} />;
    })}
    {!!modal && modal.mode !== 'overlap' && <RecordsPreviewModal mode={modal.mode} card={modal.card} userName={props.userName} showToast={showToast}
      onClose={function () { setModal(null); }} onDone={done} onOverlap={function () { setModal({ mode: 'overlap', card: modal.card }); }} />}
    {!!modal && modal.mode === 'overlap' && <RecordsOverlapModal card={modal.card} showToast={showToast}
      onClose={function () { setModal(null); }} onChanged={onChanged} />}
  </div>);
}
